import { GroupPaymentForOrder, RefundableSource } from '~/services/PaymentsService';

import { floatify, sum } from '~/utils/mathUtils';

export const CASH_SOURCE_TYPES = ['stripe', 'paypal', 'credit'];

function isCashSourceType(type: string): boolean {
  return CASH_SOURCE_TYPES.includes(type);
}

export function instrumentOwnerKey(source: Pick<RefundableSource, 'customerId' | 'email'>): string {
  return source.customerId ?? source.email ?? 'unknown';
}

function associatedContributionsForPayer(
  payerCustomerId: string,
  groupPayment: Pick<GroupPaymentForOrder, 'customerId' | 'contributions'>,
): GroupPaymentForOrder['contributions'] {
  const isLead = groupPayment.customerId === payerCustomerId;

  return groupPayment.contributions.filter((contribution) => {
    if (isLead) {
      return contribution.role === 'lead' || contribution.collectedBy === 'lead';
    }

    return contribution.collectedBy === 'participant' && contribution.customerId === payerCustomerId;
  });
}

export function associatedCashRemainingForPayer(
  payerCustomerId: string,
  groupPayment: Pick<GroupPaymentForOrder, 'customerId' | 'contributions'>,
): number {
  return sum(
    associatedContributionsForPayer(payerCustomerId, groupPayment).map((contribution) =>
      Math.max(0, floatify(contribution.amountPaid - contribution.amountRefunded)),
    ),
  );
}

export function maxCashAllocationForSource({
  source,
  otherCashAllocated,
  groupPayment,
}: {
  source: RefundableSource;
  otherCashAllocated: number;
  groupPayment?: Pick<GroupPaymentForOrder, 'customerId' | 'contributions'> | null;
}): number {
  if (!groupPayment || !source.customerId || !isCashSourceType(source.type)) {
    return source.refundableAmount;
  }

  const headroom = floatify(associatedCashRemainingForPayer(source.customerId, groupPayment) - otherCashAllocated);
  return Math.min(source.refundableAmount, Math.max(0, headroom));
}
